/**
 * Guard for panel renderers that rebuild a container OF INPUTS whose `change`
 * handlers call the same renderer back.
 *
 * WHY THIS EXISTS. Chrome dispatches a text input's native `change` event
 * synchronously while REMOVING the input from the DOM, whenever the input is
 * focused and holds a user-typed, not-yet-committed value at that moment. A
 * renderer shaped like
 *
 *   function renderPanel() {
 *     box.replaceChildren();          // removes the focused, dirty input
 *     ...build fresh rows whose change handlers call renderPanel()...
 *   }
 *
 * therefore re-enters ITSELF from inside its own `replaceChildren`: the
 * removal fires `change`, the handler commits the value and calls the
 * renderer again, the inner pass swaps the children out — and when the outer
 * `replaceChildren` resumes its removal loop it throws
 *
 *   NotFoundError: Failed to execute 'replaceChildren' on 'Element': The node
 *   to be removed is no longer a child of this node. Perhaps it was moved in
 *   a 'blur' event handler?
 *
 * That is not a corner case in this app: typing a storage figure and pressing
 * Enter (commit runs while the input is still focused), or typing and then
 * tapping the drawing (the canvas `pointerdown` handler repaints every panel
 * BEFORE the browser moves focus), both put a focused dirty input under an
 * in-flight rebuild. Reproduced live against the storage-needs panel; the
 * wall-heights and helper-line renderers share the exact shape.
 *
 * WHAT THE WRAPPER DOES. A call that arrives while a pass is already running
 * is not executed re-entrantly; it sets a flag, the in-flight pass finishes
 * its DOM swap undisturbed, and then the renderer runs once more from the
 * latest model state. The mid-removal `change` handler still commits to the
 * model synchronously — only the REPAINT is deferred, so the trailing pass
 * paints the committed value and the final DOM equals what a clean render of
 * the final model would produce.
 *
 * Deliberately synchronous (no microtask): the trailing pass runs before the
 * wrapped call returns, so callers never observe a window where the model and
 * the panel disagree.
 *
 * CAUTION for callers: pass an ANONYMOUS function. In
 * `const renderX = nonReentrantRender(function renderX() { ... })` the inner
 * `renderX()` self-calls resolve to the named function expression — the RAW,
 * unguarded renderer — and the wrapper is silently bypassed. Use
 * `const renderX = nonReentrantRender(() => { ... })` so self-calls go through
 * the guarded binding. tests/ui/non-reentrant-render.test.js pins this.
 *
 * @param {() => void} fn the renderer to protect (return value is discarded —
 *        deferred passes could not deliver one anyway)
 * @returns {() => void} the guarded renderer
 */
export function nonReentrantRender(fn) {
  let running = false;
  let queued = false;
  return function run() {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      fn();
      // Trailing repaint: drains every re-entrant request from this pass in
      // one go. A repeat is only possible if the trailing pass itself gets
      // re-entered, which needs a fresh focused-dirty input mid-removal — the
      // input that caused the first deferral is already gone by then.
      while (queued) {
        queued = false;
        fn();
      }
    } finally {
      running = false;
      queued = false;
    }
  };
}
